import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { JobAction } from '../../../action.types';
import { TooltipDirective } from '@intranet/components/tooltip/tooltip.directive';
import { UserService } from '@intranet/modules/users/user.service';
import { PlannedBlockService } from '../planned-block.service';
import { PlannedBlockViewModel } from '../planned-block.types';
import { addDays, formatDurationHM, formatHours, formatTimeRange, isSameDay } from '../planner-date.util';

// The Planned flag's tooltip: the action's blocks from today on, soonest
// first, grouped by day (see onPlannedFlagEnter).
interface PlannedTipRow {
    time: string;
    duration: string;
    person: string;
}

// A busy day's line for one person instead of their blocks.
interface PlannedTipPerson {
    person: string;
    blocks: string;
    total: string;
}

interface PlannedTipDay {
    day: string;
    isToday: boolean;
    // The day's planned time, beside its name.
    total: string;
    // Its blocks, when it has only a few — otherwise `people` (one line
    // each), so a busy day stays a few lines long.
    rows: PlannedTipRow[];
    people: PlannedTipPerson[];
    // People beyond those listed.
    morePeople: number;
}

interface PlannedTip {
    days: PlannedTipDay[];
    // Blocks on later days than the ones listed.
    more: number;
    // More than one person's blocks: each row says whose.
    showPeople: boolean;
    failed: boolean;
}

// The Actions page's Planned flag: a calendar-tick on actions with a block
// today or later (action.hasPlannedBlock), on the list's rows and the
// board's cards. Hovering lists the action's next blocks. They're loaded on
// the first hover and kept until the action is reloaded; the tooltip stays
// off until they're in (tip), then opens already its full size, so it sits
// in the right place. Click: `open` (the Planned blocks dialog).
@Component({
    selector: 'planned-flag',
    templateUrl: './planned-flag.component.html',
    styleUrls: ['./planned-flag.component.scss'],
})
export class PlannedFlagComponent implements OnChanges, OnDestroy {
    @Input() action: JobAction;
    @Output() open = new EventEmitter<JobAction>();

    @ViewChild(TooltipDirective) private _tooltip?: TooltipDirective;

    // The first few days with blocks (today first, when it has any), each
    // with all of its blocks; later days are only counted (more).
    private static readonly PLANNED_TIP_MAX_DAYS = 3;
    // A day with more blocks than this lists one line per person instead
    // (at most PLANNED_TIP_MAX_PEOPLE of them).
    private static readonly PLANNED_TIP_MAX_BLOCK_ROWS = 4;
    private static readonly PLANNED_TIP_MAX_PEOPLE = 4;

    tip: PlannedTip | null = null;
    private _loading: Subscription | null = null;
    private _hovering = false;

    constructor(
        private _plannedBlockService: PlannedBlockService,
        private _userService: UserService,
        private _changeRef: ChangeDetectorRef
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        // Another (or reloaded) action: its blocks may differ.
        if (changes.action) this.reset();
    }

    ngOnDestroy(): void {
        this._loading?.unsubscribe();
    }

    onEnter(): void {
        this._hovering = true;
        if (this.tip || this._loading) return;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this._loading = this._plannedBlockService.getActionBlocks(this.action.jobActionId, today).subscribe({
            next: (blocks) => this.setTip(this.toPlannedTip(blocks)),
            error: () => this.setTip({ days: [], more: 0, showPeople: false, failed: true }),
        });
    }

    onLeave(): void {
        this._hovering = false;
    }

    onClick(): void {
        // The dialog can move, remove or log them.
        this._tooltip?.hideTooltip(true);
        this.reset();
        this.open.emit(this.action);
    }

    private reset(): void {
        this._loading?.unsubscribe();
        this._loading = null;
        this.tip = null;
    }

    private setTip(tip: PlannedTip): void {
        this._loading = null;
        this.tip = tip;
        // Still over it: open now (tooltipDisabled has to update first).
        if (!this._hovering) return;
        this._changeRef.detectChanges();
        this._tooltip?.showTooltip(true);
    }

    private toPlannedTip(blocks: PlannedBlockViewModel[]): PlannedTip {
        const today = new Date();
        const tomorrow = addDays(today, 1);
        const users = this._userService.users || [];
        const nameOf = (userId: string) => {
            const user = users.find((u) => u.id === userId);
            return user ? user.firstName || user.fullName : '';
        };

        // The first few days with blocks, soonest first (as they come).
        const byDay: PlannedBlockViewModel[][] = [];
        for (const b of blocks) {
            const last = byDay[byDay.length - 1];
            if (last && isSameDay(new Date(last[0].start), new Date(b.start))) {
                last.push(b);
            } else if (byDay.length < PlannedFlagComponent.PLANNED_TIP_MAX_DAYS) {
                byDay.push([b]);
            } else {
                break;
            }
        }

        const minutes = (list: PlannedBlockViewModel[]) => list.reduce((sum, b) => sum + b.durationMinutes, 0);
        const days: PlannedTipDay[] = byDay.map((dayBlocks) => {
            const start = new Date(dayBlocks[0].start);
            const day: PlannedTipDay = {
                day: isSameDay(start, today) ? 'Today'
                    : isSameDay(start, tomorrow) ? 'Tomorrow'
                    : start.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }),
                isToday: isSameDay(start, today),
                total: `${formatHours(minutes(dayBlocks))}h`,
                rows: [],
                people: [],
                morePeople: 0,
            };
            if (dayBlocks.length <= PlannedFlagComponent.PLANNED_TIP_MAX_BLOCK_ROWS) {
                day.rows = dayBlocks.map((b) => {
                    const blockStart = new Date(b.start);
                    return {
                        time: formatTimeRange(blockStart, new Date(blockStart.getTime() + b.durationMinutes * 60000)),
                        duration: formatDurationHM(b.durationMinutes),
                        person: nameOf(b.userId),
                    };
                });
            } else {
                // In the order their first block that day comes.
                const perPerson = new Map<string, PlannedBlockViewModel[]>();
                dayBlocks.forEach((b) => perPerson.set(b.userId, [...(perPerson.get(b.userId) || []), b]));
                const people = Array.from(perPerson.entries()).map(([userId, list]) => ({
                    person: nameOf(userId),
                    blocks: `${list.length} ${list.length === 1 ? 'block' : 'blocks'}`,
                    total: `${formatHours(minutes(list))}h`,
                }));
                day.people = people.slice(0, PlannedFlagComponent.PLANNED_TIP_MAX_PEOPLE);
                day.morePeople = people.length - day.people.length;
            }
            return day;
        });

        const shown = byDay.reduce((sum, list) => sum + list.length, 0);
        return {
            days,
            more: blocks.length - shown,
            // One person's blocks only: no names (nothing to tell apart).
            showPeople: new Set(blocks.map((b) => b.userId)).size > 1,
            failed: false,
        };
    }
}
